import { parseTime } from '~/utils/date-helper';
import chatbotRegex from '~/utils/chatbot-regex';
import xssFilter from '~/utils/xss-filter';
import { MESSAGE_REGEX, MESSAGE_SCHEMA } from './constants';
import { TicketComment, Message } from './types';

export const decideNick = ({ sender, userName }: { sender: string; userName: string }): string => {
  const isAcha = sender === 'ACHA';
  const isCsAgent = sender.includes('CS ');

  if (sender === userName) return 'visitor';
  return isAcha || isCsAgent ? 'agent' : 'visitor';
};

export const decideDisplayName = ({ sender }: { sender: string }): string => {
  /**
   * For attachment messages we'll get a raw sender name like "CS Asev Revanzki uploaded"
   * We have to remove the ' uploaded' word from the string
   */
  if (sender.endsWith(' uploaded')) return sender.replace(' uploaded', '');
  return sender;
};

/**
 * This would handle the proper splitting of the comment.plain_body
 * We can't just split by '\n\n' and go with it
 */
export const composeMessageObject = ({
  commentBody,
  createdAt,
  userName,
}: {
  commentBody: string;
  createdAt: Date;
  userName: string;
}): Message[] => {
  const splitResult = commentBody.split('\n');

  const result: Message[] = splitResult.reduce((prev: Message[], curr: string) => {
    const trimmedPart = curr.trim();

    const beginning = trimmedPart.match(MESSAGE_REGEX.beginning);
    const ending = trimmedPart.match(MESSAGE_REGEX.ending);
    const isNbsp = trimmedPart === '&nbsp;';
    const chatbotCommand = trimmedPart.match(chatbotRegex);
    const messagePart = trimmedPart.match(MESSAGE_REGEX.messagePart);
    const messageAttachment = trimmedPart.match(MESSAGE_REGEX.messageAttachment);
    const attachmentUrl = trimmedPart.match(MESSAGE_REGEX.attachmentUrl);
    const attachmentType = trimmedPart.match(MESSAGE_REGEX.attachmentType);
    const attachmentSize = trimmedPart.match(MESSAGE_REGEX.attachmentSize);

    // Skip if is is a plain '&nbsp;', chatbot command, chat beginning, or ending
    if (isNbsp || chatbotCommand || beginning || ending) return prev;

    // If the message has these common format:
    // Example: "(17:13:36) CS Asev Revanzki: Hi ini agent Preprod, ada yang bisa saya bantu?"
    // Example: "(21:20:39) CS Asev Revanzki uploaded: Screenshot 2023-11-20 at 21.20.26.png"
    const commonPartMatches = messagePart || messageAttachment || [];
    if (commonPartMatches.length) {
      const [chatTime, sender, msg] = commonPartMatches;
      const payload: Message = {
        ...MESSAGE_SCHEMA,
        msg: xssFilter(msg.trim()),
        nick: decideNick({ sender, userName }),
        displayName: decideDisplayName({ sender }),
        timestamp: parseTime(chatTime, createdAt, true, false).getTime(),
      };
      return [...prev, payload];
    }

    // If the message has a different format, it is probably an attachment URL, Type, or Size
    const prevMessages: Message[] = [...prev];
    const lastMessage: Message = { ...MESSAGE_SCHEMA, ...prevMessages.pop() };

    if (attachmentUrl || attachmentType || attachmentSize) {
      const modifiedLastMessage: Message = {
        ...lastMessage,
        imgUrl: lastMessage.imgUrl || attachmentUrl?.[1] || '',
        attachmentType: lastMessage.attachmentType || attachmentType?.[1] || '',
        attachmentSize: lastMessage.attachmentSize || attachmentSize?.[1] || '',
      };

      return [...prevMessages, modifiedLastMessage];
    }

    // If the current message has other format, just concatenate into the lastMessage.msg
    const modifiedLastMessage: Message = {
      ...lastMessage,
      msg: xssFilter(`${lastMessage.msg}\n${trimmedPart}`),
    };

    return [...prevMessages, modifiedLastMessage];
  }, []);

  return result;
};

/**
 * This is the main function where the chat transacript would be parsed
 * The parsed message will then be displayed in the mitra-chat inbox page
 */
const parseConversation = ({ comment, userName }: { comment: TicketComment; userName: string }): Message[] => {
  if (!comment.public) return [];

  const { body, created_at: createdAt } = comment;
  const trimmedBody = body.trim();

  const result: Message[] = composeMessageObject({
    commentBody: trimmedBody,
    createdAt: new Date(createdAt),
    userName,
  });

  return result;
};

export default parseConversation;
